/**
 * User Data context for Instagram Commentor
 *
 * Centralizes app-level user data fetching (accounts, etc.)
 * Keeps AuthContext focused on authentication only.
 * Uses Firestore real-time listeners with REST API fallback.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { useAuth } from './AuthContext';
import api, {
    AccountInfo,
    AccountWithFullContext,
    UserContextResponse,
    SessionCommentSummary,
    SessionWithDetails,
    PostedCommentSummary,
} from '../services/api';
import { subscribeToUserAccounts } from '../services/firestoreListeners';
import { isFirebaseInitialized } from '../config/firebase';

// Cached session data returned by findSessionInContext
export interface CachedSessionData {
    accountUrl: string;
    session: SessionWithDetails;
    comments: SessionCommentSummary[];
}

// User data context type
interface UserDataContextType {
    instagramAccountsTriggered: AccountInfo[];
    instagramAccountsOwning: AccountInfo[]; // Accounts user owns (posted via VM)
    accountsLoading: boolean;
    contextLoading: boolean; // Whether fullContext is still being fetched from REST API
    refreshAccounts: () => Promise<void>;
    // Full context data
    fullContext: UserContextResponse | null;
    triggeredAccountsWithContext: AccountWithFullContext[];
    // Real-time status
    isUsingRealtime: boolean;
    // Convenience accessors
    getSessionComments: (accountId: string, sessionId: string) => SessionCommentSummary[] | null;
    getPostedComments: (accountId: string) => PostedCommentSummary[] | null;
    isUrlPosted: (accountId: string, url: string) => boolean;
    findSessionInContext: (sessionId: string) => CachedSessionData | null;
}

// Create context
const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

// Provider component
interface UserDataProviderProps {
    children: ReactNode;
}

export const UserDataProvider: React.FC<UserDataProviderProps> = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [instagramAccountsTriggered, setInstagramAccountsTriggered] = useState<AccountInfo[]>([]);
    const [instagramAccountsOwning, setInstagramAccountsOwning] = useState<AccountInfo[]>([]);
    const [accountsLoading, setAccountsLoading] = useState(true);
    const [contextLoading, setContextLoading] = useState(true);
    const [fullContext, setFullContext] = useState<UserContextResponse | null>(null);
    const [isUsingRealtime, setIsUsingRealtime] = useState(false);

    // Track listener unsubscribe functions
    const accountsUnsubRef = useRef<(() => void) | null>(null);
    const sessionUnsubRefs = useRef<Map<string, () => void>>(new Map());

    // Fetch user's instagram accounts using consolidated context endpoint
    const refreshAccounts = useCallback(async (): Promise<void> => {
        if (!user) {
            setInstagramAccountsTriggered([]);
            setInstagramAccountsOwning([]);
            setFullContext(null);
            setAccountsLoading(false);
            setContextLoading(false);
            return;
        }

        setAccountsLoading(true);
        setContextLoading(true);
        try {
            // Use consolidated context endpoint with all nested data
            const context = await api.getUserContext({
                includeSessions: true,
                sessionsLimit: 10,
                includePendingComments: true,
                pendingCommentsLimit: 100,
                includeSessionComments: true,
                sessionCommentsLimit: 100,
                includeDiscoveredUsers: true,
                discoveredUsersLimit: 100,
                includePostedComments: true,
                postedCommentsLimit: 100,
                includePostedUrls: true,
            });

            setFullContext(context);

            // Convert to simple AccountInfo for backwards compatibility
            const triggered: AccountInfo[] = context.triggeredAccounts.map(acc => ({
                accountId: acc.accountId,
                accountUrl: acc.accountUrl,
                sessionCount: acc.sessionCount,
                lastActivity: acc.lastActivity,
            }));
            setInstagramAccountsTriggered(triggered);
            setInstagramAccountsOwning(context.owningAccounts);
        } catch (err) {
            console.error('Failed to fetch user context:', err);
            setInstagramAccountsTriggered([]);
            setInstagramAccountsOwning([]);
            setFullContext(null);
        } finally {
            setAccountsLoading(false);
            setContextLoading(false);
        }
    }, [user]);

    // Cleanup all Firestore listeners
    const cleanupListeners = useCallback(() => {
        if (accountsUnsubRef.current) {
            accountsUnsubRef.current();
            accountsUnsubRef.current = null;
        }
        sessionUnsubRefs.current.forEach((unsub) => unsub());
        sessionUnsubRefs.current.clear();
        setIsUsingRealtime(false);
    }, []);

    // Set up Firestore listeners for real-time updates
    const setupRealtimeListeners = useCallback(() => {
        if (!user?.uid || !isFirebaseInitialized()) {
            console.log('[UserDataContext] Cannot set up realtime - no user or Firebase not initialized');
            return false;
        }

        console.log('[UserDataContext] Setting up Firestore real-time listeners');

        // Subscribe to user's triggered accounts
        accountsUnsubRef.current = subscribeToUserAccounts(
            user.uid,
            (accounts) => {
                console.log('[UserDataContext] Real-time accounts update:', accounts.length);
                setInstagramAccountsTriggered(accounts);
                setAccountsLoading(false);
                setIsUsingRealtime(true);
            },
            (error) => {
                console.error('[UserDataContext] Accounts listener error, falling back to REST:', error);
                cleanupListeners();
                // Fall back to REST API
                refreshAccounts();
            }
        );

        return true;
    }, [user?.uid, cleanupListeners, refreshAccounts]);

    // Fetch accounts when user changes (login/logout)
    useEffect(() => {
        if (authLoading) {
            // Wait for auth to complete
            return;
        }

        // Cleanup previous listeners
        cleanupListeners();

        if (user) {
            // Try to set up real-time listeners first
            const realtimeSetup = setupRealtimeListeners();

            if (!realtimeSetup) {
                // Fall back to REST API
                refreshAccounts();
            } else {
                // Also fetch full context via REST for additional data (owning accounts, etc.)
                // This is a one-time fetch, not polling
                refreshAccounts();
            }
        } else {
            // No user, clear data
            setInstagramAccountsTriggered([]);
            setInstagramAccountsOwning([]);
            setFullContext(null);
            setAccountsLoading(false);
            setContextLoading(false);
        }

        return cleanupListeners;
    }, [user, authLoading, setupRealtimeListeners, cleanupListeners, refreshAccounts]);

    // Convenience accessor: Get session comments from cached context
    const getSessionComments = useCallback((accountId: string, sessionId: string): SessionCommentSummary[] | null => {
        if (!fullContext) return null;
        const account = fullContext.triggeredAccounts.find(a => a.accountId === accountId);
        if (!account) return null;
        const session = account.recentSessions.find(s => s.id === sessionId);
        if (!session) return null;
        return session.comments.length > 0 ? session.comments : null;
    }, [fullContext]);

    // Convenience accessor: Get posted comments for an account
    const getPostedComments = useCallback((accountId: string): PostedCommentSummary[] | null => {
        if (!fullContext) return null;
        const account = fullContext.triggeredAccounts.find(a => a.accountId === accountId);
        if (!account) return null;
        return account.postedComments.length > 0 ? account.postedComments : null;
    }, [fullContext]);

    // Convenience accessor: Check if URL was posted for an account
    const isUrlPosted = useCallback((accountId: string, url: string): boolean => {
        if (!fullContext) return false;
        const account = fullContext.triggeredAccounts.find(a => a.accountId === accountId);
        if (!account) return false;
        return account.postedUrls.includes(url);
    }, [fullContext]);

    // Convenience accessor: Find a session by ID across all cached accounts
    const findSessionInContext = useCallback((sessionId: string): CachedSessionData | null => {
        if (!fullContext) return null;
        for (const account of fullContext.triggeredAccounts) {
            const session = account.recentSessions.find(s => s.id === sessionId);
            if (session) {
                return {
                    accountUrl: account.accountUrl,
                    session,
                    comments: session.comments,
                };
            }
        }
        return null;
    }, [fullContext]);

    const value: UserDataContextType = {
        instagramAccountsTriggered,
        instagramAccountsOwning,
        accountsLoading,
        contextLoading,
        refreshAccounts,
        fullContext,
        triggeredAccountsWithContext: fullContext?.triggeredAccounts ?? [],
        isUsingRealtime,
        getSessionComments,
        getPostedComments,
        isUrlPosted,
        findSessionInContext,
    };

    return <UserDataContext.Provider value={value}>{children}</UserDataContext.Provider>;
};

// Hook to use user data context
export const useUserData = (): UserDataContextType => {
    const context = useContext(UserDataContext);
    if (context === undefined) {
        throw new Error('useUserData must be used within a UserDataProvider');
    }
    return context;
};

export default UserDataContext;
